import {
  Box,
  Container,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchContext } from '@/shared/contexts/SearchContext';
import { SearchFilters, OfferSearchResult } from '../types/search.types';
import { searchService } from '../services/searchService';
import { ProductFilter } from './ProductFilter';
import { ConditionFilter } from './ConditionFilter';
import { AddressFilter } from './AddressFilter';
import { DistanceFilter } from './DistanceFilter';
import { AvailabilityFilter } from './AvailabilityFilter';
import { SearchButton } from './SearchButton';
import { SearchResults } from './SearchResults';
import { KeywordSearchField } from './KeywordSearchField';

function getDefaultEndDate(): Date {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

function shouldAutoSearchFromLatestOffers(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('fromLatestOffers') === '1';
}

function shouldOpenMapView(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('view') === 'map';
}

interface CityMapPrefilter {
  city: string;
  latitude?: number;
  longitude?: number;
}

function getCityMapPrefilter(): CityMapPrefilter | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get('fromCityMap') !== '1') {
    return null;
  }

  const city = params.get('city')?.trim();
  if (!city) {
    return null;
  }

  const latRaw = params.get('lat');
  const lngRaw = params.get('lng');
  const latitude = latRaw ? Number(latRaw) : undefined;
  const longitude = lngRaw ? Number(lngRaw) : undefined;

  return {
    city,
    latitude: Number.isFinite(latitude) ? latitude : undefined,
    longitude: Number.isFinite(longitude) ? longitude : undefined,
  };
}

export function SearchPage() {
  const { t } = useTranslation();
  const {
    searchContext,
    setSearchContext,
    setScrollPosition,
    getSearchContext,
    setViewMode,
    clearSearchContext,
  } = useSearchContext();
  const autoSearchFromLatestOffers = shouldAutoSearchFromLatestOffers();
  const cityMapPrefilter = useMemo(() => getCityMapPrefilter(), []);
  const autoSearchFromCityMap = !!cityMapPrefilter;
  const autoOpenMapView = shouldOpenMapView();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [results, setResults] = useState<OfferSearchResult[]>([]);
  const [allResults, setAllResults] = useState<OfferSearchResult[]>([]);
  const [isLoadingAllResults, setIsLoadingAllResults] = useState(false);
  const [allResultsFiltersKey, setAllResultsFiltersKey] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasMore: false,
  });
  const [currentFilters, setCurrentFilters] = useState<SearchFilters | null>(null);
  const previousMaterialId = useRef<string | undefined>(undefined);
  const isRestoringRef = useRef(false);
  const hasAutoSearchedRef = useRef(false);
  const [isKeywordSearching, setIsKeywordSearching] = useState(false);
  const [restoredKeyword, setRestoredKeyword] = useState('');

  const defaultFilterValues: SearchFilters = useMemo(
    () => ({
      keyword: '',
      materialId: '',
      materialCategory: '',
      materialSubcategory: '',
      condition: 'all',
      address: '',
      latitude: undefined,
      longitude: undefined,
      maxDistance: 25,
      distanceEnabled: false,
      startDate: null,
      endDate: getDefaultEndDate(),
      availabilityEnabled: false,
      page: 1,
      limit: 5,
    }),
    []
  );

  const { control, handleSubmit, watch, setValue, trigger, getValues, formState, reset } =
    useForm<SearchFilters>({
      defaultValues: defaultFilterValues,
    });

  // Reset filters only (keep keyword)
  const handleResetFilters = useCallback(() => {
    const currentKeyword = getValues('keyword');
    reset({ ...defaultFilterValues, keyword: currentKeyword });
    setDateError(null);
    setResults([]);
    setHasSearched(false);
    setCurrentFilters(null);
    setPagination({ currentPage: 1, totalPages: 1, total: 0, hasMore: false });
    setAllResults([]);
    setAllResultsFiltersKey('');
    previousMaterialId.current = undefined;
  }, [defaultFilterValues, getValues, reset]);

  // Clear everything when leaving the search page
  useEffect(() => {
    return () => {
      clearSearchContext();
    };
  }, [clearSearchContext]);

  // Watch fields used to enable/disable search button
  const materialId = watch('materialId');
  const availabilityEnabled = watch('availabilityEnabled');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Search button is disabled if:
  // 1. Availability filter is enabled but dates are incomplete
  // 2. Date validation currently has errors
  const isSearchDisabled =
    (availabilityEnabled && (!startDate || !endDate)) ||
    !!(formState.errors.startDate || formState.errors.endDate);

  const onSubmit = useCallback(
    async (data: SearchFilters) => {
      // Clear previous date error
      setDateError(null);

      // Validate availability filter requirements
      if (data.availabilityEnabled) {
        // "To" date is required when availability filter is enabled
        if (!data.endDate) {
          setDateError(t('search.availability.toRequiredWhenEnabled'));
          return;
        }

        // Validate date range if both dates exist
        if (data.startDate && data.endDate) {
          const startDate = new Date(data.startDate);
          const endDate = new Date(data.endDate);

          if (startDate > endDate) {
            setDateError(t('search.availability.dateRangeError'));
            return;
          }
        }
      }

      setIsLoading(true);
      setHasSearched(true);

      // Reset map cache synchronously with setCurrentFilters so they all batch
      // into the same render — prevents the map effect from loading stale data
      // and then getting immediately wiped when these arrive after the await.
      const searchFilters = { ...data, page: 1, limit: 5 };
      setCurrentFilters(searchFilters);
      setAllResults([]);
      setAllResultsFiltersKey('');

      try {
        const response = await searchService.searchOffers(searchFilters);
        setResults(response.data);
        setPagination({
          currentPage: response.meta.page,
          totalPages: response.meta.totalPages,
          total: response.meta.total,
          hasMore: response.meta.page < response.meta.totalPages,
        });

        // Save to context for navigation back
        setSearchContext(searchFilters, response.data, response.meta.total);
      } catch (error) {
        console.error('Error searching offers:', error);
        setResults([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          total: 0,
          hasMore: false,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setSearchContext, t]
  );

  // Restore search context on mount only
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    if (autoSearchFromLatestOffers || autoSearchFromCityMap) {
      return;
    }

    const context = getSearchContext();
    if (context.filters && context.results.length > 0) {
      isRestoringRef.current = true;

      // Restore filters in form
      Object.entries(context.filters).forEach(([key, value]) => {
        if (key === 'keyword') {
          setRestoredKeyword((value as string) || '');
        }
        setValue(key as keyof SearchFilters, value);
      });

      // Restore results and pagination
      setResults(context.results);
      setCurrentFilters(context.filters);
      setHasSearched(true);
      setPagination({
        currentPage: Math.ceil(context.results.length / 5),
        totalPages: Math.ceil(context.totalResults / 5),
        total: context.totalResults,
        hasMore: context.results.length < context.totalResults,
      });
      previousMaterialId.current = context.filters.materialId;

      // Reset flag after restoration completes
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 0);
    }
  }, [autoSearchFromCityMap, autoSearchFromLatestOffers, getSearchContext, setValue]);

  useEffect(() => {
    if ((!autoSearchFromLatestOffers && !autoSearchFromCityMap) || hasAutoSearchedRef.current) {
      return;
    }

    hasAutoSearchedRef.current = true;

    if (!autoSearchFromCityMap || !cityMapPrefilter) {
      void onSubmit(getValues());
      return;
    }

    const nextFilters: SearchFilters = {
      ...getValues(),
      address: cityMapPrefilter.city,
      latitude: cityMapPrefilter.latitude,
      longitude: cityMapPrefilter.longitude,
      distanceEnabled:
        cityMapPrefilter.latitude !== undefined && cityMapPrefilter.longitude !== undefined,
      maxDistance: 25,
    };

    setValue('address', nextFilters.address);
    setValue('latitude', nextFilters.latitude);
    setValue('longitude', nextFilters.longitude);
    setValue('distanceEnabled', nextFilters.distanceEnabled);
    setValue('maxDistance', nextFilters.maxDistance);

    void onSubmit(nextFilters);
  }, [
    autoSearchFromCityMap,
    autoSearchFromLatestOffers,
    cityMapPrefilter,
    getValues,
    onSubmit,
    setValue,
  ]);

  useEffect(() => {
    if (autoOpenMapView) {
      setViewMode('map');
    }
  }, [autoOpenMapView, setViewMode]);

  // Restore scroll position after results are rendered
  useLayoutEffect(() => {
    if (results.length > 0 && searchContext.scrollPosition > 0 && scrollContainerRef.current) {
      window.scrollTo(0, searchContext.scrollPosition);
    }
  }, [results, searchContext.scrollPosition]);

  // Clear results when material selection changes (new search)
  useEffect(() => {
    // Skip clearing during restoration
    if (isRestoringRef.current) {
      return;
    }

    if (previousMaterialId.current !== undefined && previousMaterialId.current !== materialId) {
      setResults([]);
      setHasSearched(false);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        total: 0,
        hasMore: false,
      });
    }
    previousMaterialId.current = materialId;
  }, [materialId]);

  // Called by KeywordSearchField after its internal debounce
  const handleKeywordSearch = useCallback(
    (newKeyword: string) => {
      setValue('keyword', newKeyword);
      const data = getValues();
      setIsKeywordSearching(true);
      void onSubmit(data).finally(() => setIsKeywordSearching(false));
    },
    [getValues, onSubmit, setValue]
  );

  // Save scroll position before navigation
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrollPosition]);

  // Load all results for map view when switching to map mode or when filters change
  const filtersKey = currentFilters ? JSON.stringify(currentFilters) : '';
  useEffect(() => {
    if (searchContext.viewMode !== 'map' || !currentFilters || !hasSearched) return;
    // Skip if already loaded (or currently loading) for these exact filters
    if (allResultsFiltersKey === filtersKey) return;

    const loadAllResults = async () => {
      setIsLoadingAllResults(true);
      try {
        const mapFilters = { ...currentFilters, page: 1, limit: 100 };
        const response = await searchService.searchOffers(mapFilters);
        setAllResults(response.data);
        setAllResultsFiltersKey(filtersKey);
      } catch (error) {
        console.error('Error loading map results:', error);
        setAllResults([]);
      } finally {
        setIsLoadingAllResults(false);
      }
    };

    loadAllResults();
  }, [
    searchContext.viewMode,
    filtersKey,
    currentFilters,
    hasSearched,
    allResultsFiltersKey,
    // NOTE: allResults.length intentionally excluded — we gate on filtersKey only.
    // Including it caused the effect to re-run when data arrived, creating a race
    // condition where stale markers were wiped and sometimes not re-added.
  ]);

  // Handler for updating favorite status in search results
  const handleFavoriteUpdate = useCallback(
    (offerId: string, isFavorite: boolean) => {
      // Update the local results state
      setResults(prevResults => {
        const updatedResults = prevResults.map(offer =>
          offer.id === offerId ? { ...offer, isFavorite } : offer
        );

        // Update the search context with the new results
        if (currentFilters) {
          setSearchContext(currentFilters, updatedResults, pagination.total);
        }

        return updatedResults;
      });
    },
    [currentFilters, pagination.total, setSearchContext]
  );

  const handleLoadMore = async () => {
    if (!currentFilters || isLoadingMore || !pagination.hasMore) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const nextPage = pagination.currentPage + 1;
      const searchFilters = { ...currentFilters, page: nextPage, limit: 5 };

      const response = await searchService.searchOffers(searchFilters);

      // Append new results to existing ones
      const updatedResults = [...results, ...response.data];
      setResults(updatedResults);
      setPagination({
        currentPage: response.meta.page,
        totalPages: response.meta.totalPages,
        total: response.meta.total,
        hasMore: response.meta.page < response.meta.totalPages,
      });

      // Update context with accumulated results
      setSearchContext(searchFilters, updatedResults, response.meta.total);
    } catch (error) {
      console.error('Error loading more offers:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <>
      <Container maxWidth="md" sx={{ py: 3 }} ref={scrollContainerRef}>
        {/* Keyword Search Field */}
        <KeywordSearchField
          initialValue={restoredKeyword}
          isSearching={isKeywordSearching}
          onSearch={handleKeywordSearch}
          onClear={() => {
            setValue('keyword', '');
            const data = getValues();
            void onSubmit({ ...data, keyword: '' });
          }}
        />

        {/* Hint between keyword and filters */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          {t('search.keyword.filterHint')}
        </Typography>

        {/* Filters Section - Collapsible */}
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          <Accordion
            expanded={isFiltersExpanded}
            onChange={(_, expanded) => setIsFiltersExpanded(expanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'white',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  pr: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'inherit' }}>
                  {t('search.filters')}
                </Typography>
                <Tooltip title={t('search.resetFilters')}>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleResetFilters();
                    }}
                    sx={{ color: 'inherit' }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Product Filter */}
                <ProductFilter control={control} />

                {/* Condition Filter */}
                <ConditionFilter control={control} />

                {/* Address Filter */}
                <AddressFilter control={control} setValue={setValue} />

                {/* Distance Filter */}
                <DistanceFilter control={control} watch={watch} />

                {/* Availability Filter */}
                <AvailabilityFilter
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  getValues={getValues}
                  dateError={dateError}
                  onClearError={() => setDateError(null)}
                />

                {/* Search Button */}
                <SearchButton
                  isLoading={isLoading}
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSearchDisabled}
                />
              </form>
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* Results Section */}
        {hasSearched && (
          <SearchResults
            results={results}
            allResults={allResults}
            isLoading={isLoading}
            isLoadingAllResults={isLoadingAllResults}
            onLoadMore={handleLoadMore}
            hasMore={pagination.hasMore}
            isLoadingMore={isLoadingMore}
            pagination={pagination}
            searchFilters={currentFilters || undefined}
            onFavoriteUpdate={handleFavoriteUpdate}
          />
        )}
      </Container>
    </>
  );
}
